import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";

import { RobotoButton } from "@/shared/components";
import { useCreateCollection } from "@/shared/domain/collections/hooks/useCreateCollection";
import { TagInput, Textarea, TextInput } from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http";

import styles from "./AddToCollection.module.css";
import { resourcesOf, resourceTypeOf } from "./addToCollectionHelpers";
import {
  CollectionFormValues,
  collectionFormSchema,
} from "./collectionFormSchema";
import { SelectedResources } from "./selectedResources";

interface CreateFormProps {
  onCancel: () => void;
  onSuccess: () => void;
  orgId: string;
  selected: SelectedResources;
}

export function CreateForm({
  onCancel,
  onSuccess,
  orgId,
  selected,
}: CreateFormProps) {
  const { mutate: createCollection, isPending, error } = useCreateCollection();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<CollectionFormValues>({
    resolver: zodResolver(collectionFormSchema),
    defaultValues: {
      name: "",
      description: "",
      tags: [],
      resourceType: resourceTypeOf(selected),
    },
  });

  const onSubmit = (values: CollectionFormValues) => {
    if (!orgId) {
      return;
    }
    createCollection(
      {
        orgId,
        name: values.name,
        description: values.description || undefined,
        tags: values.tags.length ? values.tags : undefined,
        resourceType: values.resourceType,
        resources: resourcesOf(selected),
      },
      { onSuccess },
    );
  };

  return (
    <form
      className={styles.createForm}
      onSubmit={(e) => void handleSubmit(onSubmit)(e)}
    >
      {error && (
        <div className={styles.errorBanner}>
          {userFacingErrorMessage(error)}
        </div>
      )}
      <div className={styles.field}>
        <label className={styles.fieldLabel}>
          Name<span className={styles.req}>*</span>
        </label>
        <TextInput
          {...register("name")}
          autoFocus
          autoComplete="off"
          placeholder="e.g. Urban edge cases"
        />
        {errors.name && (
          <span className={styles.fieldError}>{errors.name.message}</span>
        )}
      </div>
      <div className={styles.field}>
        <label className={styles.fieldLabel}>Description</label>
        <Textarea
          {...register("description")}
          autoComplete="off"
          placeholder="What is this collection for?"
        />
      </div>
      <div className={styles.field}>
        <label className={styles.fieldLabel}>Tags</label>
        <Controller
          control={control}
          name="tags"
          render={({ field }) => (
            <TagInput
              initialTags={field.value}
              onChange={field.onChange}
              aria-label="Tags"
            />
          )}
        />
      </div>
      <div className={styles.createActions}>
        <RobotoButton
          type="button"
          variant="text"
          onClick={onCancel}
          eventName="CreateCollectionBackClicked"
        >
          Back
        </RobotoButton>
        <RobotoButton
          type="submit"
          variant="contained"
          color="secondary"
          disabled={isPending}
          eventName="CreateCollectionClicked"
        >
          {isPending ? "Creating…" : "Create & add"}
        </RobotoButton>
      </div>
    </form>
  );
}
